import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function EditProductDialog({ product, open, onOpenChange, onSuccess }) {
  const [name, setName] = useState(product?.product_name || '');
  const [supplier, setSupplier] = useState(product?.supplier || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Product name is required'); return; }
    setSaving(true);
    try {
      await api.put(`/products/${encodeURIComponent(product.barcode)}`, {
        product_name: name.trim(),
        supplier: supplier.trim()
      });
      toast.success('Product updated');
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Edit Product</DialogTitle>
          <DialogDescription>
            Barcode: <span className="font-mono font-semibold">{product?.barcode}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="field-label">Product Name</label>
            <Input
              data-testid="edit-product-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-none border-zinc-300 focus:border-emerald-600 focus-visible:ring-0"
            />
          </div>
          <div>
            <label className="field-label">Supplier</label>
            <Input
              data-testid="edit-product-supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              className="rounded-none border-zinc-300 focus:border-emerald-600 focus-visible:ring-0"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-zinc-300">Cancel</Button>
          <Button
            data-testid="edit-product-save"
            onClick={handleSave}
            disabled={saving}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeactivateDialog({ product, open, onOpenChange, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const handleDeactivate = async () => {
    setLoading(true);
    try {
      await api.put(`/products/${encodeURIComponent(product.barcode)}/deactivate`);
      toast.success(`${product.product_name} deactivated`);
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Deactivate Product
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to deactivate <strong>{product?.product_name}</strong>?
            It will be hidden from default results but historical sales data will be preserved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-zinc-300">Cancel</Button>
          <Button
            data-testid="confirm-deactivate-btn"
            onClick={handleDeactivate}
            disabled={loading}
            className="rounded-none bg-amber-500 hover:bg-amber-600 text-white"
          >
            {loading ? 'Deactivating...' : 'Yes, Deactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ActivateDialog({ product, open, onOpenChange, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const handleActivate = async () => {
    setLoading(true);
    try {
      await api.put(`/products/${encodeURIComponent(product.barcode)}/activate`);
      toast.success(`${product.product_name} activated`);
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Reactivate Product</DialogTitle>
          <DialogDescription>
            Reactivate <strong>{product?.product_name}</strong>? It will appear in default results again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-zinc-300">Cancel</Button>
          <Button
            data-testid="confirm-activate-btn"
            onClick={handleActivate}
            disabled={loading}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {loading ? 'Activating...' : 'Reactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteDialog({ product, open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/products/${encodeURIComponent(product.barcode)}?confirm=PERMANENTLY_DELETE`);
      toast.success(`${product.product_name} permanently deleted`);
      onSuccess?.();
      onOpenChange(false);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setLoading(false); }
  };

  const handleClose = (v) => { onOpenChange(v); if (!v) setStep(1); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none sm:rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: "'Chivo', sans-serif" }}>
            <Trash2 className="h-5 w-5" />
            Delete Product Permanently
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <>
            <DialogDescription className="text-sm">
              This will <strong className="text-red-600">permanently delete</strong> the product
              <strong> {product?.product_name}</strong> (barcode: <span className="font-mono">{product?.barcode}</span>)
              and <strong className="text-red-600">all its sales history</strong>. This cannot be undone.
            </DialogDescription>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} className="rounded-none border-zinc-300">Cancel</Button>
              <Button
                data-testid="delete-step1-btn"
                onClick={() => setStep(2)}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              >
                I understand, continue
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              <strong>Final confirmation:</strong> All sales data for this product will be permanently erased.
              Consider using <em>Deactivate</em> instead to preserve history.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} className="rounded-none border-zinc-300">Cancel</Button>
              <Button
                data-testid="delete-confirm-final-btn"
                onClick={handleDelete}
                disabled={loading}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
